import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  ArrowDropDown,
  ArrowDropUp,
  FilterAlt,
  FilterAltOutlined,
} from "@mui/icons-material";

import {
  formatDate,
  getComparisonFormatTimeDimension,
} from "../../components/Reports/Components/utils";
import { useGlobalContext } from "../../context/global";
import useUserDetails from "../../helper/userData/useUserDetails";
import useInitialLoad from "../../hooks/useInitialLoad";
import useWrapperInsightsQuery from "../../hooks/useWrapperInsightsQuery";
import {
  mergeCustomOutputToAPIFiltersFormatWithComparisonPercentage,
  getComparisonPercentageFiltersRuleTree,
  transformToTopXArray,
} from "../../helper/Quadrant/quadrantHelper";
import { processFields } from "../../components/Filter/subComponents/filterHelper";

import Loader from "../../components/loader";
import SectionTitle from "../../components/sectionTitle";
import Breadcrumb from "../../components/Breadcrumb/ReportsBreadCrumb";
import BulkActionBarV2 from "../../components/Bars/BulkActionBarV2";
import BulkActionConfirmModalV2 from "../../components/modal/BulkActionConfirmModalV2";
import BulkAdjustBidModalV2 from "../../components/modal/BulkAdjustBidModalV2";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import PageVersionSwitchButton from "../../components/Buttons/PageVersionSwitchButton";
import {
  ColumnCustomizationButton,
  COLUMN_CUSTOMIZATION_PAGE_IDS,
  V2_STANDARD_VIEW_OPTIONS,
  usePageColumnCustomization,
} from "../../components/ColumnCustomization";
import {
  QuickFilterButton,
  QUICK_FILTER_PAGE_IDS,
} from "../../components/QuickFilter";

import ExportDownloadTooltip, {
  EXPORT_LIMIT_TOOLTIP_MESSAGE,
  EXPORT_TRACK_TOOLTIP_MESSAGE,
  getExportRedirectIconButtonSx,
} from "../../components/Reports/Components/ExportDownloadTooltip";
import ComplexFilterWidget from "../../components/Filter/ComplexFilterWidget";
import AppliedFiltersChips from "../../components/Filter/AppliedFiltersChips";

import TargetingV2Table from "./TargetingV2Table";
import {
  FiltersCampaignDataTargetingV2,
  FiltersDimensionDataTargetingV2,
  FiltersMeasuresDataTargetingV2,
  getTopXDataConfigTargetingV2,
  TARGETING_V2_ALWAYS_MEASURES,
  TARGETING_V2_DEFAULT_DIMENSIONS,
  TARGETING_V2_GROUP_BY_OPTIONS,
  TARGETING_V2_PAGE_MEASURES,
  getTargetingV2Columns,
} from "./targetingV2Configs";
import {
  buildTargetingInsightsQueryBody,
  clearAllSelections,
  getTargetingSearchMembers,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSP,
  normalizeTargetingInsightRows,
  prepareWrapperFilters,
  SB_SD_OPTIMISATION_MODAL_NOTE,
} from "./targetingV2LocalFilters";
import { getTargetingV2BidRules } from "./targetingV2BidConfig";
import {
  buildTargetingV2WrapperExportQueryBody,
  exportTargetingV2Report,
  getTargetingV2VisibleColumnKeys,
} from "../../helper/PageWiseExportLogic/targetingV2Export";
import { openRequestTrackingPage } from "../../helper/common/openRequestTrackingPage";
import {
  BULK_SCOPE,
  applyBulkRowToggle,
  applyBulkSelectAllPage,
  applySelectAllMatching,
  clearBulkSelection,
  getRowBulkId,
  getSelectedRowIds,
  shouldShowSelectAllMatchingBanner,
} from "../../helper/bulkSelection/bulkSelectionModes";
import { buildBulkQuery } from "../../helper/bulkSelection/buildBulkQuery";
import {
  buildBulkTargetingUpdateBody,
  getRequestedCountForBulk,
  submitBulkTargetingUpdate,
} from "../../helper/bulkSelection/bulkTargetingUpdate";
import { useAuthHeader } from "react-auth-kit";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];
const REPORT_API = process.env.REACT_APP_REPORT_API;
const USER_PLATFORM = "amazon";
const TARGETING_BULK_ID_KEYS = ["target_id", "ad_group_id", "campaign_id"];

const TargetingV2 = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const location = useLocation();
  const viewTYPE =
    new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
  const authHeader = useAuthHeader();
  const token = authHeader();
  const {
    calendarDate,
    applyCalendar,
    clientId,
    multiCalendarDate,
    setHeaderCalendarHandleComparisonView,
    setHeaderCalendarHideComparisonSwitch,
  } = useGlobalContext();
  const [userDetails] = useUserDetails();
  const [initialLoad, setInitialLoad] = useInitialLoad();

  // Keep in sync with calendar URL param (`?isComparisonView=true`) — same as ProductReport.
  const initialIsComparisonView =
    new URLSearchParams(window.location.search).get("isComparisonView") ===
    "true";

  const [showComplexFilter, setShowComplexFilter] = useState(false);
  const [filterModuleFilters, setFilterModuleFilters] = useState({});
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [insightsMenuAnchor, setInsightsMenuAnchor] = useState(null);
  const [isComparisonView, setIsComparisonView] = useState(
    initialIsComparisonView
  );

  const queryDimensions = TARGETING_V2_DEFAULT_DIMENSIONS;
  const groupByFilter = TARGETING_V2_GROUP_BY_OPTIONS[0]?.value;
  const queryMeasure = TARGETING_V2_PAGE_MEASURES;
  const [queryOrder, setQueryOrder] = useState([["spends", "desc"]]);
  const allColumns = useMemo(() => getTargetingV2Columns(isComparisonView), [isComparisonView]);


  const {
    currentViewType,
    selectedMetricKeys,
    columnCollectionId,
    metricColumns,
    columnCollections,
    visibleTableColumns,
    isSaving: isColumnCustomizationSaving,
    syncFromViewType,
    handleApply: handleApplyColumnCustomization,
    handleSave: handleSaveColumnCustomization,
    handleSaveAs: handleSaveAsColumnCustomization,
    handleRenameView: handleRenameColumnView,
    handleDeleteView: handleDeleteColumnView,
  } = usePageColumnCustomization({
    pageId: COLUMN_CUSTOMIZATION_PAGE_IDS.TARGETING_V2,
    allColumns,
    standardViewOptions: V2_STANDARD_VIEW_OPTIONS,
    initialViewType: viewTYPE,
    platform: USER_PLATFORM,
    seedWithAllMetrics: true,
  });

  useEffect(() => {
    const viewtype =
      new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
    syncFromViewType(viewtype);
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("viewtype", currentViewType);
    const newSearch = queryParams.toString();
    const currentSearch = window.location.search.replace(/^\?/, "");
    if (newSearch === currentSearch) return;
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${newSearch}`
    );
  }, [currentViewType]);


  const [queryTimeDimensions, setQueryTimeDimensions] = useState(() => {
    if (initialIsComparisonView) {
      return [
        {
          dimension: "date",
          compareDateRange: [
            [
              formatDate(multiCalendarDate.selection1.startDate),
              formatDate(multiCalendarDate.selection1.endDate),
            ],
            [
              formatDate(multiCalendarDate.selection2.startDate),
              formatDate(multiCalendarDate.selection2.endDate),
            ],
          ],
        },
      ];
    }
    return [
      {
        dimension: "date",
        dateRange: [
          formatDate(calendarDate[0].startDate),
          formatDate(calendarDate[0].endDate),
        ],
      },
    ];
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [paginationLimit, setPaginationLimit] = useState(50);
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const {
    rows: dataSource,
    setRows: setDataSource,
    summaryData,
    totalCount,
    isLoading: isLoaderVisible,
    runQuery,
    resetResult,
  } = useWrapperInsightsQuery({
    errorMessage: "Failed to fetch targeting data",
  });
  const [isExportDownloading, setIsExportDownloading] = useState(false);
  const [isExportRedirectMode, setIsExportRedirectMode] = useState(false);

  const [bulkScope, setBulkScope] = useState(BULK_SCOPE.SELECTED);
  const [bulkExcludeIds, setBulkExcludeIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [isBulkAdjustModalOpen, setIsBulkAdjustModalOpen] = useState(false);
  const [pendingBulkOperation, setPendingBulkOperation] = useState(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [isBulkSuccess, setIsBulkSuccess] = useState(false);

  const filterWidgetConfig = useMemo(
    () => ({
      dimension: FiltersDimensionDataTargetingV2[USER_PLATFORM],
      measure: FiltersMeasuresDataTargetingV2[USER_PLATFORM],
      campaigns: FiltersCampaignDataTargetingV2[USER_PLATFORM],
      topXConfig: getTopXDataConfigTargetingV2(USER_PLATFORM),
    }),
    []
  );

  useEffect(() => {
    const init = async () => {
      const dimensionConfig = FiltersDimensionDataTargetingV2[USER_PLATFORM];
      const measureConfig = FiltersMeasuresDataTargetingV2[USER_PLATFORM];
      const campaignConfig = FiltersCampaignDataTargetingV2[USER_PLATFORM];
      if (dimensionConfig) {
        dimensionConfig.fields = await processFields(
          dimensionConfig.fields ?? [],
          clientId,
          []
        );
      }
      if (measureConfig) {
        measureConfig.fields = await processFields(
          measureConfig.fields ?? [],
          clientId,
          []
        );
      }
      if (campaignConfig) {
        campaignConfig.fields = await processFields(
          campaignConfig.fields ?? [],
          clientId,
          []
        );
      }
    };
    if (clientId) init();
  }, [clientId]);

  // ---- Comparison view + header calendar wiring (same pattern as ProductReport) ----
  const handleComparisonView = useCallback(
    (isComparison) => {
      setCurrentPage(1);
      if (isComparison) {
        setQueryTimeDimensions([
          {
            dimension: "date",
            compareDateRange: [
              [
                formatDate(multiCalendarDate.selection1.startDate),
                formatDate(multiCalendarDate.selection1.endDate),
              ],
              [
                formatDate(multiCalendarDate.selection2.startDate),
                formatDate(multiCalendarDate.selection2.endDate),
              ],
            ],
          },
        ]);
      } else {
        setQueryTimeDimensions([
          {
            dimension: "date",
            dateRange: [
              formatDate(calendarDate[0].startDate),
              formatDate(calendarDate[0].endDate),
            ],
          },
        ]);
      }
      setIsComparisonView(isComparison);
    },
    [calendarDate, multiCalendarDate]
  );

  useEffect(() => {
    setHeaderCalendarHandleComparisonView(() => handleComparisonView);
    setHeaderCalendarHideComparisonSwitch(false);
    return () => {
      setHeaderCalendarHandleComparisonView(null);
      setHeaderCalendarHideComparisonSwitch(false);
    };
  }, [
    setHeaderCalendarHandleComparisonView,
    setHeaderCalendarHideComparisonSwitch,
    handleComparisonView,
  ]);

  useEffect(() => {
    if (isComparisonView) {
      const startDate = formatDate(multiCalendarDate.selection1.startDate);
      const endDate = formatDate(multiCalendarDate.selection1.endDate);
      const startDatePrevious = formatDate(multiCalendarDate.selection2.startDate);
      const endDatePrevious = formatDate(multiCalendarDate.selection2.endDate);
      const apiDateRange = queryTimeDimensions[0]?.compareDateRange;
      if (
        !apiDateRange ||
        startDate !== apiDateRange[0][0] ||
        endDate !== apiDateRange[0][1] ||
        startDatePrevious !== apiDateRange[1][0] ||
        endDatePrevious !== apiDateRange[1][1]
      ) {
        setQueryTimeDimensions([
          {
            dimension: "date",
            compareDateRange: [
              [startDate, endDate],
              [startDatePrevious, endDatePrevious],
            ],
          },
        ]);
        setCurrentPage(1);
      }
    } else {
      const startDate = formatDate(calendarDate[0].startDate);
      const endDate = formatDate(calendarDate[0].endDate);
      const apiDateRange = queryTimeDimensions[0]?.dateRange;
      if (!apiDateRange || startDate !== apiDateRange[0] || endDate !== apiDateRange[1]) {
        setQueryTimeDimensions([{ dimension: "date", dateRange: [startDate, endDate] }]);
        setCurrentPage(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyCalendar]);

  useEffect(() => {
    setIsExportRedirectMode(false);
    setIsBulkSuccess(false);
  }, [filterModuleFilters, queryTimeDimensions, queryOrder, searchValue, isComparisonView]);

  const selectedTargeting = useMemo(
    () => dataSource.filter((row) => row?.isChecked),
    [dataSource]
  );
  const selectedBulkIds = useMemo(
    () => getSelectedRowIds(dataSource, TARGETING_BULK_ID_KEYS),
    [dataSource]
  );
  const pageSelectableCount = useMemo(
    () => dataSource.filter((row) => isCampaignTypeSP(row?.campaign_type)).length,
    [dataSource]
  );
  const showSelectAllMatching = shouldShowSelectAllMatchingBanner({
    scope: bulkScope,
    pageSelectedCount: selectedTargeting.length,
    pageSelectableCount,
    totalCount,
  });

  const resetBulkSelectionState = () => {
    setDataSource((prev) => clearBulkSelection(prev).rows);
    setBulkScope(BULK_SCOPE.SELECTED);
    setBulkExcludeIds([]);
  };

  // Clear selection + exit all-matching on page / filter / search / sort / date / page-size.
  useEffect(() => {
    setBulkScope(BULK_SCOPE.SELECTED);
    setBulkExcludeIds([]);
    setDataSource((prev) => clearAllSelections(prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    paginationLimit,
    filterModuleFilters,
    searchValue,
    queryOrder,
    queryTimeDimensions,
    isComparisonView,
  ]);

  function getSelectedSearchDimensions() {
    return getTargetingSearchMembers(queryDimensions);
  }

  function getPlaceholderText() {
    return "Search Targeting, Campaign Name, Ad Group, Portfolio…";
  }

  function mergeTargetingFilters(moduleFilters) {
    return mergeCustomOutputToAPIFiltersFormatWithComparisonPercentage(
      moduleFilters?.metrics?.custom_output,
      moduleFilters?.dimension?.custom_output,
      moduleFilters?.campaigns?.custom_output,
      moduleFilters?.topX?.custom_output,
      isComparisonView
        ? moduleFilters?.comparisonPercentage?.custom_output
        : null
    );
  }

  /** Global condition (topX) → API `percentile_filters`, same as ProductReport. */
  function getPercentileFilters(mergedOrModuleTopX) {
    return transformToTopXArray(mergedOrModuleTopX) ?? [];
  }

  function buildExtraQueryFields(mergedFilters) {
    const extraQueryFields = {
      percentile_filters: getPercentileFilters(mergedFilters?.topX),
    };
    if (isComparisonView) {
      const comparisonPercentageFilters =
        getComparisonPercentageFiltersRuleTree(
          mergedFilters?.comparisonPercentage
        );
      if (comparisonPercentageFilters) {
        extraQueryFields.comparison_percentage_filters =
          comparisonPercentageFilters;
      }
    }
    return extraQueryFields;
  }

  function handleDataFetch(moduleFilters) {
    if (!clientId) return;

    const mergedFilters = mergeTargetingFilters(moduleFilters);
    const { filters: transformedFilters, isEmptyMatch } =
      prepareWrapperFilters(mergedFilters);

    if (isEmptyMatch) {
      resetResult();
      setInitialLoad(false);
      return;
    }

    const filtersWithSearch = injectSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSelectedSearchDimensions()
    );
    const extraQueryFields = buildExtraQueryFields(mergedFilters);

    runQuery(async ({ signal }) => {
      const timeDimensionsForApi = isComparisonView
        ? getComparisonFormatTimeDimension(queryTimeDimensions)
        : queryTimeDimensions;

      const queryBody = buildTargetingInsightsQueryBody({
        queryDimensions,
        filters: filtersWithSearch,
        queryMeasure,
        queryOrder,
        timeDimensions: timeDimensionsForApi,
        offset: (currentPage - 1) * paginationLimit,
        limit: paginationLimit,
        extraQueryFields,
      });

      const summaryQueryBody = {
        query: {
          ...queryBody.query,
          measures: [
            ...new Set([...(queryMeasure || []), ...TARGETING_V2_ALWAYS_MEASURES]),
          ],
        },
      };

      const insightsPath = isComparisonView ? "target:comparison" : "target:insights";
      const summaryPath = isComparisonView
        ? "target:comparison-summary"
        : "target:insights-summary";
      const insightsUrl = `${REPORT_API}/client/${clientId}/${insightsPath}?source=amazon`;
      const summaryUrl = `${REPORT_API}/client/${clientId}/${summaryPath}?source=amazon`;

      const [insightsRes, summaryRes] = await Promise.all([
        axios.post(insightsUrl, queryBody, { signal }),
        axios.post(summaryUrl, summaryQueryBody, { signal }),
      ]);

      const summary = summaryRes?.data ?? {};
      return {
        rows: normalizeTargetingInsightRows(insightsRes?.data?.data ?? []),
        summaryData: summary,
        totalCount: Number(summary?.total_count) || 0,
      };
    }).finally(() => {
      setInitialLoad(false);
    });
  }

  useEffect(() => {
    handleDataFetch(filterModuleFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterModuleFilters,
    queryTimeDimensions,
    queryOrder,
    currentPage,
    paginationLimit,
    searchValue,
    searchTrigger,
    clientId,
    isComparisonView,
  ]);

  const handleFilterOutput = (filters) => {
    const nextFilters = filters || {};
    setFilterModuleFilters(nextFilters);
    setIsFilterApplied(
      (nextFilters?.dimension?.data?.length ?? 0) > 0 ||
        (nextFilters?.metrics?.data?.length ?? 0) > 0 ||
        (nextFilters?.campaigns?.data?.length ?? 0) > 0 ||
        (nextFilters?.topX?.data?.length ?? 0) > 0 ||
        (nextFilters?.comparisonPercentage?.data?.length ?? 0) > 0
    );
    setShowComplexFilter(false);
    setCurrentPage(1);
  };

  const FilterComponent = (
    <ComplexFilterWidget
      key={`targeting_v2_${showComplexFilter}`}
      config={filterWidgetConfig}
      campaignFilter={false}
      showDimensionFilter={true}
      filters={filterModuleFilters}
      showPopupModal={showComplexFilter}
      setShowPopupModal={setShowComplexFilter}
      output={handleFilterOutput}
      prefixMember={""}
      type="page"
      sidebar={true}
      default={"metrics"}
      page={"overlay"}
      isAppliedCallback={() => {}}
      summary={"always"}
      narrowFilters={true}
      showComparisonPercentageFilter={isComparisonView}
      summaryOutput={() => {}}
      calloutTitle="Select your filters"
      calloutContent="+ Add Filter"
      hideFilterSummary={true}
    />
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / paginationLimit));

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handlePageLimitChange = (event) => {
    setPaginationLimit(Number(event.target.value));
    setCurrentPage(1);
  };

  const singleCheckBoxHandler = (index) => {
    setDataSource((prev) => {
      const result = applyBulkRowToggle({
        rows: prev,
        index,
        scope: bulkScope,
        excludeIds: bulkExcludeIds,
        idKeys: TARGETING_BULK_ID_KEYS,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };
  const allCheckBoxHandler = (checked) => {
    setDataSource((prev) => {
      const result = applyBulkSelectAllPage({
        rows: prev,
        checked,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };
  const removeAllSelected = () => {
    resetBulkSelectionState();
  };

  const handleSelectAllMatching = () => {
    setDataSource((prev) => {
      const result = applySelectAllMatching({
        rows: prev,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };

  const getCurrentBulkFilters = () => {
    // Same merge path as list/export so comparison % filters are included.
    const mergedFilters = mergeTargetingFilters(filterModuleFilters);
    const { filters: transformedFilters } = prepareWrapperFilters(mergedFilters);
    const extraQueryFields = buildExtraQueryFields(mergedFilters);
    return {
      filters: injectSearchIntoFilters(
        transformedFilters,
        searchValue,
        getSelectedSearchDimensions()
      ),
      percentileFilters: extraQueryFields.percentile_filters,
      comparisonPercentageFilters:
        extraQueryFields.comparison_percentage_filters,
    };
  };

  const prepareBulkQuery = ({
    scopeOverride,
    selectedRowsOverride,
    excludeRowsOverride,
  } = {}) => {
    const scope = scopeOverride || bulkScope;
    const selectedRows = selectedRowsOverride || selectedTargeting;
    const excludeRows =
      excludeRowsOverride ||
      dataSource.filter((row) =>
        bulkExcludeIds.includes(getRowBulkId(row, TARGETING_BULK_ID_KEYS))
      );
    const dimensionsForBulk = [
      ...new Set([
        ...(queryDimensions || []),
        "target_id",
        "ad_group_id",
        "campaign_id",
      ]),
    ];
    const measuresForBulk = [
      ...new Set([
        ...(queryMeasure || []),
        ...TARGETING_V2_ALWAYS_MEASURES,
        "spends",
        "bid",
        "cpc",
      ]),
    ];
    const {
      filters: currentFilters,
      percentileFilters,
      comparisonPercentageFilters,
    } = getCurrentBulkFilters();
    let baseFilters = currentFilters;
    if (
      scope === BULK_SCOPE.ALL_MATCHING ||
      scope === BULK_SCOPE.ALL_MATCHING_EXCEPT
    ) {
      baseFilters = injectSpCampaignTypeFilter(baseFilters);
    }
    const timeDimensionsForApi = isComparisonView
      ? getComparisonFormatTimeDimension(queryTimeDimensions)
      : queryTimeDimensions;
    return buildBulkQuery({
      scope,
      baseFilters,
      selectedRows,
      excludeRows,
      idKeys: TARGETING_BULK_ID_KEYS,
      queryContext: {
        dimensions: dimensionsForBulk,
        measures: measuresForBulk,
        order: queryOrder,
        timeDimensions: timeDimensionsForApi,
        percentile_filters: percentileFilters,
        ...(comparisonPercentageFilters
          ? {
              comparison_percentage_filters: comparisonPercentageFilters,
            }
          : {}),
      },
    });
  };

  const handleToggleStatus = (row) => {
    if (isBulkSubmitting) return;
    if (!isCampaignTypeSP(row?.campaign_type)) return;

    const currentStatus = String(row?.status ?? "").toUpperCase();
    const nextStatus = currentStatus === "ENABLED" ? "PAUSED" : "ENABLED";

    setIsBulkSuccess(false);
    setPendingBulkOperation({
      type: "status",
      status: nextStatus,
      rows: [row],
      scope: BULK_SCOPE.SELECTED,
    });
    setBulkConfirm({
      actionKey: nextStatus === "PAUSED" ? "Pause" : "Enable",
      title:
        nextStatus === "PAUSED"
          ? "Pause this targeting?"
          : "Enable this targeting?",
      actionLabel: nextStatus === "PAUSED" ? "Pause" : "Enable",
      description: null,
      status: nextStatus,
    });
  };

  // ---- SelectionBar / bulk actions ----
  const handleSelectionBarClick = (optionKey) => {
    if (isBulkSubmitting) return;

    if (optionKey === "Adjust Bid") {
      setPendingBulkOperation(null);
      setIsBulkSuccess(false);
      setIsBulkAdjustModalOpen(true);
      return;
    }

    const status = optionKey === "Pause" ? "PAUSED" : "ENABLED";
    setIsBulkSuccess(false);
    setPendingBulkOperation({ type: "status", status });
    setBulkConfirm({
      actionKey: optionKey,
      title:
        status === "PAUSED"
          ? "Pause selected targetings?"
          : "Enable selected targetings?",
      actionLabel: optionKey,
      description: null,
      status,
    });
  };

  const handleBulkAdjustModalSubmit = (operation) => {
    setPendingBulkOperation({
      type: "bid",
      ...operation,
    });
    const isCopyCpc = operation?.adjustmentType === "COPY_CPC";
    const valueText = isCopyCpc
      ? "Copy CPC"
      : operation?.adjustmentType?.includes("PERCENTAGE")
        ? `${operation.value}%`
        : String(operation.value);
    setBulkConfirm({
      actionKey: "Adjust Bid",
      title: "Confirm bulk bid adjustment?",
      actionLabel: "Confirm",
      description: (
        <>
          Apply{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {operation.label}
          </Box>
          {!isCopyCpc ? (
            <>
              {" "}
              (
              <Box component="span" sx={{ fontWeight: 700 }}>
                {valueText}
              </Box>
              )
            </>
          ) : null}{" "}
          to the selected targetings.
        </>
      ),
    });
  };

  const handleBulkConfirm = async () => {
    if (!bulkConfirm || !pendingBulkOperation || isBulkSubmitting) {
      setBulkConfirm(null);
      return;
    }

    const operationRows = pendingBulkOperation.rows;
    const operationScope = pendingBulkOperation.scope || bulkScope;
    const isSingleRowStatus = Boolean(operationRows?.length);

    const requestedCount = isSingleRowStatus
      ? operationRows.length
      : getRequestedCountForBulk({
          scope: bulkScope,
          selectedIdsCount: selectedBulkIds.length,
          totalCount,
          excludeIdsCount: bulkExcludeIds.length,
        });

    if (requestedCount <= 0) {
      toast.info("No targetings selected for bulk update.");
      setBulkConfirm(null);
      setPendingBulkOperation(null);
      return;
    }

    const bulkPayload = prepareBulkQuery({
      scopeOverride: operationScope,
      selectedRowsOverride: operationRows,
      excludeRowsOverride: isSingleRowStatus ? [] : undefined,
    });
    const email = userDetails?.user?.email ?? localStorage.getItem("email") ?? "";

    const body =
      pendingBulkOperation.type === "status"
        ? buildBulkTargetingUpdateBody({
            query: bulkPayload?.query,
            email,
            requestedCount,
            status: pendingBulkOperation.status,
          })
        : buildBulkTargetingUpdateBody({
            query: bulkPayload?.query,
            email,
            requestedCount,
            adjustmentType: pendingBulkOperation.adjustmentType,
            value: pendingBulkOperation.value,
          });

    setBulkConfirm(null);
    setIsBulkAdjustModalOpen(false);
    setIsBulkSubmitting(true);
    setInitialLoad(true);

    const wasStatusUpdate = pendingBulkOperation.type === "status";

    try {
      await submitBulkTargetingUpdate({
        clientId,
        userPlatform: USER_PLATFORM,
        skipEvents: true,
        body,
        token,
      });
      setPendingBulkOperation(null);
      if (!isSingleRowStatus) {
        removeAllSelected();
      }
      setIsBulkSuccess(true);
      setBulkConfirm({
        actionKey: wasStatusUpdate ? "Status" : "Adjust Bid",
        title: "Request Submitted",
        actionLabel: "Close",
        description: null,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to submit bulk targeting update."
      );
      setBulkConfirm(null);
    } finally {
      setIsBulkSubmitting(false);
      setInitialLoad(false);
    }
  };

  const isPercentageFieldsDisabled =
    bulkScope === BULK_SCOPE.SELECTED &&
    selectedTargeting?.some(
      (row) => row?.bid === null || row?.bid === undefined || row?.bid === ""
    );

  // ---- Export ----
  const handleExportClick = async () => {
    if (isExportRedirectMode) {
      openRequestTrackingPage();
      return;
    }

    const exportTotalCount = totalCount || dataSource?.length || 0;
    if (exportTotalCount <= 0) {
      toast.info("No records available for export.");
      return;
    }

    const mergedFilters = mergeTargetingFilters(filterModuleFilters);
    const { filters: transformedFilters, isEmptyMatch } =
      prepareWrapperFilters(mergedFilters);
    if (isEmptyMatch) {
      toast.info("No records available for export.");
      return;
    }

    const filtersWithSearch = injectSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSelectedSearchDimensions()
    );

    const timeDimensionsForApi = isComparisonView
      ? getComparisonFormatTimeDimension(queryTimeDimensions)
      : queryTimeDimensions;

    const insightsQueryBody = buildTargetingInsightsQueryBody({
      queryDimensions,
      filters: filtersWithSearch,
      queryMeasure,
      queryOrder,
      timeDimensions: timeDimensionsForApi,
      extraQueryFields: buildExtraQueryFields(mergedFilters),
    });
    delete insightsQueryBody.query.offset;
    delete insightsQueryBody.query.limit;

    await exportTargetingV2Report({
      clientId,
      userPlatform: USER_PLATFORM,
      triggeredBy:
        userDetails?.user?.email ?? localStorage.getItem("email") ?? "",
      totalCount: exportTotalCount,
      queryBody: buildTargetingV2WrapperExportQueryBody({
        query: insightsQueryBody.query,
      }),
      visibleColumnKeys: getTargetingV2VisibleColumnKeys(visibleTableColumns),
      isComparison: isComparisonView,
      setIsExporting: setIsExportDownloading,
      onExportSuccess: () => setIsExportRedirectMode(true),
    });
  };

  const analysisByOptions = TARGETING_V2_GROUP_BY_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  const targetingBidRules = useMemo(() => getTargetingV2BidRules(), []);

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box-targeting-v2"
    >
      <div className="campaign-list-container overflow-hidden">
        <BulkAdjustBidModalV2
          open={isBulkAdjustModalOpen}
          title="Adjust Collective Bid"
          subtitle="Choose how you want to update bids for the selected targetings."
          previewRows={bulkScope === BULK_SCOPE.SELECTED ? selectedTargeting : []}
          previewLabelKey="targeting"
          previewSecondaryKey="campaign_name"
          showCopyCpc
          cpcKey="cpc"
          bidRules={targetingBidRules}
          infoNote={SB_SD_OPTIMISATION_MODAL_NOTE}
          isPercentageFieldsDisabled={isPercentageFieldsDisabled}
          detailedText={
            isPercentageFieldsDisabled
              ? "Percentage disabled because selected rows contain empty bid values"
              : null
          }
          onClose={() => {
            setIsBulkAdjustModalOpen(false);
            setPendingBulkOperation(null);
          }}
          onSubmit={handleBulkAdjustModalSubmit}
        />

        <Box>
          <Breadcrumb />
          <Stack direction="row" alignItems="center">
            <SectionTitle sectionTitleHeading="Targeting" />
            <PageVersionSwitchButton pageKey="targeting" isNewVersion />
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              mt: 1,
              position: "relative",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              options={analysisByOptions}
              disableClearable
              disabled
              getOptionLabel={(option) => option?.label || ""}
              value={
                analysisByOptions.find((option) => option.value === groupByFilter) ||
                analysisByOptions[0] ||
                null
              }
              size="small"
              sx={{
                minWidth: "200px",
                "& .MuiAutocomplete-inputRoot": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiAutocomplete-input": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiSvgIcon-root": {
                  color: "#333333",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Analysis by"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                      color: "#333333",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#333333",
                    },
                    "& .MuiInputBase-root": {
                      height: "36px",
                      fontSize: "12px !important",
                      color: "#333333",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "12px !important",
                      color: "#333333",
                      padding: "6px 8px",
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  sx: {
                    maxHeight: 250,
                    overflow: "auto",
                    fontSize: "12px",
                    "& .MuiAutocomplete-option": {
                      fontSize: "12px",
                      color: "#333333",
                      padding: "6px 12px",
                      "&:hover": { backgroundColor: "#EDEDED" },
                    },
                  },
                },
              }}
            />

            <TextField
              size="small"
              placeholder={getPlaceholderText()}
              variant="outlined"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  setSearchValue(e.target.value);
                  setSearchTrigger((prev) => prev + 1);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "16px", color: "#333333" }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchValue("");
                        setInputValue("");
                        setCurrentPage(1);
                      }}
                      edge="end"
                    >
                      <CloseIcon sx={{ fontSize: "16px", color: "#333333" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flexGrow: 1,
                minWidth: 200,
                "& .MuiInputBase-root": {
                  height: "36px",
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiInputBase-input": {
                  fontSize: "12px",
                  color: "#333333",
                  padding: "6px 4px",
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "12px",
                  color: "#000000",
                  opacity: 0.6,
                },
              }}
            />


            <ColumnCustomizationButton
              collections={columnCollections}
              columns={metricColumns}
              activeCollectionId={columnCollectionId}
              selectedColumnKeys={selectedMetricKeys}
              onApply={handleApplyColumnCustomization}
              onSave={handleSaveColumnCustomization}
              onSaveAs={handleSaveAsColumnCustomization}
              onRenameView={handleRenameColumnView}
              onDeleteView={handleDeleteColumnView}
              saving={isColumnCustomizationSaving}
            />

            <QuickFilterButton
              clientId={clientId}
              platform={USER_PLATFORM}
              pageId={QUICK_FILTER_PAGE_IDS.TARGETING_V2}
              filterConfig={filterWidgetConfig}
              campaignFilter={false}
              onApply={handleFilterOutput}
            />

            <ToggleButton
              value="filters"
              selected={showComplexFilter}
              onChange={() => setShowComplexFilter(!showComplexFilter)}
              sx={{
                height: "36px",
                borderRadius: 1,
                px: 1.5,
                borderColor: showComplexFilter ? "grey.700" : "grey.400",
                backgroundColor: showComplexFilter ? "grey.100" : "white",
                color: "#333333",
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  borderColor: "grey.700",
                  backgroundColor: showComplexFilter ? "grey.200" : "grey.50",
                },
                "& .MuiTypography-root": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& svg": {
                  color: "#333333",
                },
              }}
            >
              <Badge
                overlap="circular"
                variant="dot"
                invisible={!isFilterApplied}
                sx={{
                  "& .MuiBadge-badge": {
                    bgcolor: isFilterApplied ? "primary.main" : "transparent",
                  },
                }}
              >
                {showComplexFilter ? (
                  <FilterAlt fontSize="small" />
                ) : (
                  <FilterAltOutlined fontSize="small" />
                )}
              </Badge>
              <Typography fontWeight={500} sx={{ ml: 1 }}>
                Filters
              </Typography>
              {showComplexFilter ? (
                <ArrowDropUp fontSize="small" />
              ) : (
                <ArrowDropDown fontSize="small" />
              )}
            </ToggleButton>

            <ExportDownloadTooltip
              autoOpen={isExportRedirectMode}
              title={
                isExportRedirectMode
                  ? EXPORT_TRACK_TOOLTIP_MESSAGE
                  : EXPORT_LIMIT_TOOLTIP_MESSAGE
              }
            >
              <span>
                <IconButton
                  onClick={handleExportClick}
                  disabled={isExportDownloading}
                  sx={getExportRedirectIconButtonSx(isExportRedirectMode)}
                >
                  {isExportDownloading ? (
                    <CircularProgress size={18} thickness={5} sx={{ color: "grey.600" }} />
                  ) : isExportRedirectMode ? (
                    <OpenInNew sx={{ fontSize: 20 }} />
                  ) : (
                    <FileDownloadOutlined sx={{ fontSize: 22 }} />
                  )}
                </IconButton>
              </span>
            </ExportDownloadTooltip>

            <Button
              variant="outlined"
              size="small"
              startIcon={<InsightsOutlined fontSize="small" />}
              onClick={(event) => setInsightsMenuAnchor(event.currentTarget)}
              sx={{
                height: "36px",
                textTransform: "none",
                fontSize: "12px",
                color: "#333333",
                borderColor: "grey.400",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "grey.700",
                  backgroundColor: "grey.50",
                },
              }}
            >
              View more Insights
            </Button>
            <Menu
              anchorEl={insightsMenuAnchor}
              open={Boolean(insightsMenuAnchor)}
              onClose={() => setInsightsMenuAnchor(null)}
              disableScrollLock
            >
              {[
                { label: "Match Type Report", href: "/match-type-report" },
                { label: "Targeting Group Report", href: "/targeting-group-report" },
              ].map((item) => (
                <MenuItem
                  key={item.href}
                  sx={{ fontSize: "12px" }}
                  onClick={() => {
                    window.open(item.href, "_blank", "noopener,noreferrer");
                    setInsightsMenuAnchor(null);
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {showComplexFilter && FilterComponent}
          <AppliedFiltersChips
            filters={filterModuleFilters}
            config={filterWidgetConfig}
            onChange={handleFilterOutput}
            hideSaveActions
          />

          <BulkActionBarV2
            open={
              selectedTargeting.length > 0 ||
              bulkScope === BULK_SCOPE.ALL_MATCHING ||
              bulkScope === BULK_SCOPE.ALL_MATCHING_EXCEPT
            }
            scope={bulkScope}
            selectedIdsCount={selectedBulkIds.length}
            totalCount={totalCount}
            excludeIdsCount={bulkExcludeIds.length}
            showSelectAllMatching={showSelectAllMatching}
            onSelectAllMatching={handleSelectAllMatching}
            onClear={removeAllSelected}
            actions={[
              {
                key: "Adjust Bid",
                label: isBulkSubmitting ? "Submitting..." : "Adjust Bid",
                variant: "outlined",
              },
              { key: "Pause", label: "Pause", variant: "outlined" },
              { key: "Enable", label: "Enable", variant: "contained", color: "primary" },
            ]}
            onAction={handleSelectionBarClick}
          />

          <BulkActionConfirmModalV2
            open={Boolean(bulkConfirm)}
            title={bulkConfirm?.title}
            actionLabel={
              isBulkSubmitting ? "Submitting..." : bulkConfirm?.actionLabel || "Confirm"
            }
            description={bulkConfirm?.description}
            infoNote={SB_SD_OPTIMISATION_MODAL_NOTE}
            scope={
              pendingBulkOperation?.rows?.length
                ? BULK_SCOPE.SELECTED
                : bulkScope
            }
            selectedIdsCount={
              pendingBulkOperation?.rows?.length || selectedBulkIds.length
            }
            totalCount={totalCount}
            excludeIdsCount={
              pendingBulkOperation?.rows?.length ? 0 : bulkExcludeIds.length
            }
            zIndex={1500}
            isSuccess={isBulkSuccess}
            successTitle="Request Submitted"
            successMessage="Your bulk action request has been submitted successfully. You can track it on the Request Tracking page."
            onGoToTracking={openRequestTrackingPage}
            onCancel={() => {
              if (isBulkSubmitting) return;
              setBulkConfirm(null);
              setPendingBulkOperation(null);
              setIsBulkSuccess(false);
            }}
            onConfirm={handleBulkConfirm}
          />

          <Typography variant="caption" sx={{ color: "#333333", display: "block", mt: 0.5 }}>
            SB/SD optimisation not supported*
          </Typography>

          <TargetingV2Table
            columns={visibleTableColumns}
            rows={dataSource}
            isLoading={isLoaderVisible || initialLoad}
            queryOrder={queryOrder}
            summaryData={summaryData}
            onSortClick={(key, order) => {
              setCurrentPage(1);
              setQueryOrder([[key, order]]);
            }}
            onToggleRow={singleCheckBoxHandler}
            onToggleAll={allCheckBoxHandler}
            isComparisonView={isComparisonView}
            onToggleStatus={handleToggleStatus}
          />

          {!isLoaderVisible &&
            !initialLoad &&
            (totalCount > 0 || dataSource?.length > 0) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#4f5366",
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>
                    Page Size
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={paginationLimit}
                      onChange={handlePageLimitChange}
                      disabled={isLoaderVisible}
                      sx={{ height: 36, fontSize: "12px", color: "#333333" }}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{ fontSize: "12px" }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  lazyLoading={isLoaderVisible}
                  totalItems={totalCount}
                  itemsPerPage={paginationLimit}
                />
              </Box>
            )}
        </Box>
      </div>
      {initialLoad ? <Loader /> : null}
    </div>
  );
};

export default TargetingV2;
